import React, { useState, useEffect } from 'react';
import { Button, IconButton, Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

/**
 * GenericBoard component renders a button with increment and decrement functionality,
 * along with a count display. The count is reset when the `reset` prop changes.
 * 
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.text - Text to display on the button.
 * @param {string} props.variant - The button's variant (e.g., 'contained', 'outlined').
 * @param {string} props.color - The button's color (e.g., 'primary', 'secondary').
 * @param {Function} props.onClick - Function called when the button is clicked to increment the count.
 * @param {Function} props.handleGenericDecrement - Function called to decrement the count.
 * @param {boolean} props.reset - Boolean that determines if the count should be reset to 0.
 */
const GenericBoard = ({ text, variant, color, onClick, handleGenericDecrement, reset }) => {
    const [count, setCount] = useState(0);

    /**
     * Resets the count to 0 whenever the `reset` prop changes.
     */
    useEffect(() => {
        if (reset) {
            setCount(0);
        }
    }, [reset]);

    /**
     * Handles the increment action. Calls `onClick` and if it returns true,
     * increments the count by 1.
     */
    const handleIncrement = () => {
        const added = onClick(); // Check if the module was added
        if (added) {
            setCount(prevCount => prevCount + 1);
        }
    };

    /**
     * Handles the decrement action. Calls `handleGenericDecrement` and
     * decrements the count by 1 if it's greater than 0.
     */
    const handleDecrement = () => {
        if (count > 0) {
            handleGenericDecrement(); // Remove module
            setCount(prevCount => prevCount - 1);
        }
    };

    return (
        <Box display="flex" alignContent="center" alignItems="center" sx={{ width: '100%' }}>
            <Button
                size="small"
                variant={variant || 'outlined'}
                color={color || 'primary'}
                onClick={handleIncrement} // Use handleIncrement for click action
                sx={{
                    fontFamily: 'Open Sans',
                    color: '#363636',
                    width: { xs: '100%', sm: '90%', md: '70%' }, // Responsive width
                    height: { xs: '40px', sm: '40px' }, // Responsive height
                    padding: { xs: '8px 12px', sm: '10px 16px' }, // Responsive padding
                    margin: '0 auto',
                    textTransform: 'none',
                    whiteSpace: 'normal', // Allows text wrapping
                    overflow: 'hidden', // Prevents content overflow
                    textOverflow: 'ellipsis', // Adds "..." if the text is too long
                    fontSize: 'clamp(10px, 4vw, 16px)', // Responsive font size
                    lineHeight: '1.2', // Line height for better readability
                    maxHeight: '60px' // Maximum button height
                }}
            >
                {text || 'Generic Board'}
            </Button>

            {/* Responsive count display */}
            <Typography
                variant="body1"
                sx={{
                    fontSize: 'clamp(12px, 2.5vw, 16px)', // Responsive font size
                    minWidth: '30px',
                    textAlign: 'center',
                    flexShrink: 0 // Prevents shrinking too much on small screens
                }}
            >
                {count}
            </Typography>

            {/* Increment and decrement icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <IconButton
                    onClick={handleIncrement}
                    sx={{
                        border: '1px solid #363636',
                        borderRadius: '50%',
                        padding: '5px',
                        fontSize: 'clamp(12px, 2.5vw, 16px)' // Responsive font size
                    }}
                >
                    <AddIcon />
                </IconButton>
                <IconButton
                    onClick={handleDecrement}
                    sx={{
                        border: '1px solid #363636',
                        borderRadius: '50%',
                        padding: '5px',
                        fontSize: 'clamp(12px, 2.5vw, 16px)' // Responsive font size
                    }}
                >
                    <RemoveIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default GenericBoard;
